import { usePostHog } from "posthog-js/react";
import { Button } from "@/dentlab/src/components/ui/button";
import {
	JobDocumentTypeEnum,
	mapJobDocumentTypeToColor,
} from "../../job-document/job-document.types";
import { TooltipWrapper } from "@/dentlab/src/components/ui/tooltip-wrapper";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { JobStatusEnum } from "@/lib/types/job";

interface JobDocumentCreationGridProps {
	onCreateDocument: (type: JobDocumentTypeEnum) => void;
	onCreateFromTemplate: (type: JobDocumentTypeEnum) => void;
	onOpenVendorDialog: () => void;
}

interface CreationButtonProps {
	label: string;
	onClick: () => void;
	disabled: boolean;
	disabledReason?: string;
	tooltip?: string;
	testId: string;
	bgColor: string;
}

const CreationButton: React.FC<CreationButtonProps> = ({
	label,
	onClick,
	disabled,
	disabledReason,
	tooltip,
	testId,
	bgColor,
}) => {
	const button = (
		<button
			onClick={onClick}
			disabled={disabled}
			className="w-full h-7 px-2 inline-flex items-center justify-start rounded-md text-xs font-medium text-white transition-colors hover:opacity-90 disabled:pointer-events-none disabled:opacity-50"
			style={{ backgroundColor: bgColor }}
			data-testid={testId}
			aria-label={label}
		>
			<span className="truncate">{label}</span>
		</button>
	);

	const tooltipText = disabled && disabledReason ? disabledReason : tooltip;
	if (tooltipText) {
		return (
			<TooltipWrapper title={tooltipText} position="right">
				<span className="w-full">{button}</span>
			</TooltipWrapper>
		);
	}

	return button;
};

export const JobDocumentCreationGrid: React.FC<
	JobDocumentCreationGridProps
> = ({ onCreateDocument, onCreateFromTemplate, onOpenVendorDialog }) => {
	const postHog = usePostHog();
	const { isCreateAllowed } = useJobStore((state) => ({
		isCreateAllowed: state.isCreateJobDocumentAllowed,
		// jobStatus: state.job?.status,
	}));
	// const preRegTooltip =
	// 	jobStatus === JobStatusEnum.PRE_REGISTERED
	// 		? 'Status wechselt automatisch zu "Pendent"'
	// 		: undefined;

	const handleCreateDocument = (type: JobDocumentTypeEnum) => {
		postHog.capture("add_job_document", {
			document_type: type,
			source: "direct",
		});
		onCreateDocument(type);
	};

	const handleCreateFromTemplate = (type: JobDocumentTypeEnum) => {
		postHog.capture("add_job_document", {
			document_type: type,
			source: "template",
		});
		onCreateFromTemplate(type);
	};

	const handleOpenVendorDialog = () => {
		postHog.capture("add_job_document", {
			document_type: "vendor",
			source: "vendor_dialog",
		});
		onOpenVendorDialog();
	};
	const kvPermission = isCreateAllowed(JobDocumentTypeEnum.Quotation);
	const lsPermission = isCreateAllowed(JobDocumentTypeEnum.DeliveryNote);
	const gsPermission = isCreateAllowed(JobDocumentTypeEnum.CreditNote);
	const rvPermission = isCreateAllowed(JobDocumentTypeEnum.Tracing);

	return (
		<div className="grid grid-cols-2 gap-1 px-2 py-2">
			<CreationButton
				label="Kostenvoranschlag"
				onClick={() =>
					handleCreateDocument(JobDocumentTypeEnum.Quotation)
				}
				disabled={!kvPermission.isAllowed}
				disabledReason={kvPermission.explanation}
				// tooltip={preRegTooltip}
				testId="job-document-create-kv"
				bgColor={
					mapJobDocumentTypeToColor[JobDocumentTypeEnum.Quotation]
				}
			/>
			<CreationButton
				label="KV-Vorlage"
				onClick={() =>
					handleCreateFromTemplate(JobDocumentTypeEnum.Quotation)
				}
				disabled={!kvPermission.isAllowed}
				disabledReason={kvPermission.explanation}
				// tooltip={preRegTooltip}
				testId="job-document-create-kv-template"
				bgColor={
					mapJobDocumentTypeToColor[JobDocumentTypeEnum.Quotation]
				}
			/>
			<CreationButton
				label="Lieferschein"
				onClick={() =>
					handleCreateDocument(JobDocumentTypeEnum.DeliveryNote)
				}
				disabled={!lsPermission.isAllowed}
				disabledReason={lsPermission.explanation}
				// tooltip={preRegTooltip}
				testId="job-document-create-ls"
				bgColor={
					mapJobDocumentTypeToColor[JobDocumentTypeEnum.DeliveryNote]
				}
			/>
			<CreationButton
				label="LS-Vorlage"
				onClick={() =>
					handleCreateFromTemplate(JobDocumentTypeEnum.DeliveryNote)
				}
				disabled={!lsPermission.isAllowed}
				disabledReason={lsPermission.explanation}
				// tooltip={preRegTooltip}
				testId="job-document-create-ls-template"
				bgColor={
					mapJobDocumentTypeToColor[JobDocumentTypeEnum.DeliveryNote]
				}
			/>
			<CreationButton
				label="Gutschrift"
				onClick={() =>
					handleCreateDocument(JobDocumentTypeEnum.CreditNote)
				}
				disabled={!gsPermission.isAllowed}
				disabledReason={gsPermission.explanation}
				// tooltip={preRegTooltip}
				testId="job-document-create-gs"
				bgColor={
					mapJobDocumentTypeToColor[JobDocumentTypeEnum.CreditNote]
				}
			/>
			<CreationButton
				label="Rückverfolgung"
				onClick={() =>
					handleCreateDocument(JobDocumentTypeEnum.Tracing)
				}
				disabled={!rvPermission.isAllowed}
				disabledReason={rvPermission.explanation}
				// tooltip={preRegTooltip}
				testId="job-document-create-rv"
				bgColor={mapJobDocumentTypeToColor[JobDocumentTypeEnum.Tracing]}
			/>
			<Button
				variant="secondary"
				size="sm"
				onClick={handleOpenVendorDialog}
				className="w-full justify-start text-xs h-7 px-2"
				data-testid="job-document-create-fremdlabor"
				aria-label="Fremdlabor beauftragen"
			>
				<span className="truncate">Fremdlabor</span>
			</Button>
		</div>
	);
};
